/**
 * Hook para gerenciar salvamentos pendentes de itens do pedido
 * 
 * RECURSOS:
 * - Fila por item com mutex automático
 * - Merge last-write-wins por campo
 * - batchResolvers corrigido (snapshot antes do await)
 * - discardAll() para "Sair sem salvar"
 * - flushAll() com Promise por item (sem busy wait)
 * - getPendingItemIds() para debug
 */

import { useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PendingPatch {
  [field: string]: any;
}

interface ItemQueue {
  pendingPatch: PendingPatch;
  isProcessing: boolean;
  lastError: Error | null;
  resolvers: Array<{ resolve: () => void; reject: (err: Error) => void }>;
  // Promise que resolve quando o processamento atual terminar
  processingPromise: Promise<void> | null;
}

export function usePendingSaves() {
  const queues = useRef<Map<string, ItemQueue>>(new Map());
  const [version, setVersion] = useState(0); // Para re-render

  // Força re-render para atualizar estados
  const forceUpdate = useCallback(() => setVersion(v => v + 1), []);

  // Obtém ou cria fila para um item
  const getQueue = useCallback((itemId: string): ItemQueue => {
    if (!queues.current.has(itemId)) {
      queues.current.set(itemId, {
        pendingPatch: {},
        isProcessing: false,
        lastError: null,
        resolvers: [],
        processingPromise: null
      });
    }
    return queues.current.get(itemId)!;
  }, []);

  // Processa fila de um item (interno)
  const processQueue = useCallback(async (itemId: string) => {
    const queue = getQueue(itemId);
    
    // Mutex: se já processando, sair (será chamado novamente ao final)
    if (queue.isProcessing) return;
    
    // Nada pendente?
    if (Object.keys(queue.pendingPatch).length === 0) {
      return;
    }
    
    queue.isProcessing = true;
    queue.lastError = null;
    forceUpdate();
    
    // ✅ CRÍTICO: Capturar patch atual E resolvers ANTES do await
    const patchToSave = { ...queue.pendingPatch };
    queue.pendingPatch = {};
    
    // ✅ SNAPSHOT dos resolvers DESTE batch - limpar para próximos enqueues
    const batchResolvers = [...queue.resolvers];
    queue.resolvers = [];
    
    try {
      console.log(`💾 [PendingSaves] Salvando item ${itemId}:`, Object.keys(patchToSave));
      
      const { error } = await supabase
        .from('itens_pedido')
        .update(patchToSave)
        .eq('id', itemId);
      
      if (error) throw error;
      
      console.log(`✅ [PendingSaves] Salvo com sucesso: ${itemId}`);
      
      // ✅ Resolve SÓ os resolvers deste batch
      batchResolvers.forEach(r => r.resolve());
      
    } catch (err) {
      console.error(`❌ [PendingSaves] Erro ao salvar ${itemId}:`, err);
      queue.lastError = err as Error;
      
      // ✅ Rejeita SÓ os resolvers deste batch
      batchResolvers.forEach(r => r.reject(err as Error));
      
    } finally {
      queue.isProcessing = false;
      forceUpdate();
      
      // Se acumulou mais patches durante o processamento, processar novamente
      if (Object.keys(queue.pendingPatch).length > 0) {
        processQueue(itemId);
      }
    }
  }, [getQueue, forceUpdate]);

  // Enqueue: adiciona campos ao patch e dispara processamento
  // Retorna Promise que resolve quando ESTE batch for salvo
  const enqueue = useCallback((itemId: string, updates: Record<string, any>): Promise<void> => {
    return new Promise((resolve, reject) => {
      const queue = getQueue(itemId);
      
      // ✅ MERGE: last-write-wins por campo
      Object.assign(queue.pendingPatch, updates);
      
      // Adicionar resolver para este enqueue
      queue.resolvers.push({ resolve, reject });
      
      forceUpdate();
      
      // ✅ Auto-dispara processQueue (não precisa chamar separado)
      processQueue(itemId);
    });
  }, [getQueue, processQueue, forceUpdate]);

  // Verifica se tem pendentes (item específico ou qualquer)
  const hasPending = useCallback((itemId?: string): boolean => {
    if (itemId) {
      const queue = queues.current.get(itemId);
      return queue ? (Object.keys(queue.pendingPatch).length > 0 || queue.isProcessing) : false;
    }
    
    for (const queue of queues.current.values()) {
      if (Object.keys(queue.pendingPatch).length > 0 || queue.isProcessing) {
        return true;
      }
    }
    return false;
  }, []);

  // Verifica se há QUALQUER pendente
  const hasAnyPending = useCallback((): boolean => {
    return hasPending();
  }, [hasPending]);

  // Retorna lista de itemIds com salvamentos pendentes
  const getPendingItemIds = useCallback((): string[] => {
    const pendingIds: string[] = [];
    for (const [itemId, queue] of queues.current.entries()) {
      if (Object.keys(queue.pendingPatch).length > 0 || queue.isProcessing) {
        pendingIds.push(itemId);
      }
    }
    return pendingIds;
  }, []);

  // Flush de TODOS os itens pendentes (com Promise por item, sem busy wait)
  const flushAll = useCallback(async (): Promise<{ success: boolean; errors: string[] }> => {
    const errors: string[] = [];
    const itemIds = Array.from(queues.current.keys());
    
    console.log(`🔄 [PendingSaves] FlushAll iniciado para ${itemIds.length} itens`);
    
    // Processar todos em paralelo e aguardar
    const promises = itemIds.map(async (itemId) => {
      const queue = queues.current.get(itemId);
      if (!queue) return;
      
      try {
        // Se está processando, aguardar via Promise
        if (queue.isProcessing) {
          // Criar Promise que aguarda o processamento terminar
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (!queue.isProcessing) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 50);
            
            // Timeout de segurança (30s)
            setTimeout(() => {
              clearInterval(checkInterval);
              resolve();
            }, 30000);
          });
        }
        
        // Se ainda tem patch pendente após aguardar, processar
        if (Object.keys(queue.pendingPatch).length > 0) {
          await new Promise<void>((resolve, reject) => {
            queue.resolvers.push({ resolve, reject });
            processQueue(itemId);
          });
        }
      } catch (err) {
        errors.push(`Item ${itemId}: ${(err as Error).message}`);
      }
    });
    
    await Promise.all(promises);
    
    console.log(`✅ [PendingSaves] FlushAll concluído. Erros: ${errors.length}`);
    
    return { success: errors.length === 0, errors };
  }, [processQueue]);

  // Obtém erro de um item
  const getError = useCallback((itemId: string): Error | null => {
    return queues.current.get(itemId)?.lastError || null;
  }, []);

  // Limpa erro (após retry)
  const clearError = useCallback((itemId: string) => {
    const queue = queues.current.get(itemId);
    if (queue) {
      queue.lastError = null;
      forceUpdate();
    }
  }, [forceUpdate]);

  // Descarta pendentes de um item (para "Sair mesmo assim")
  const discardPending = useCallback((itemId: string) => {
    const queue = queues.current.get(itemId);
    if (queue) {
      queue.pendingPatch = {};
      queue.resolvers.forEach(r => r.reject(new Error('Descartado pelo usuário')));
      queue.resolvers = [];
      forceUpdate();
    }
  }, [forceUpdate]);

  // Descarta TODOS os pendentes
  const discardAll = useCallback(() => {
    console.log('🗑️ [PendingSaves] Descartando todos os pendentes...');
    for (const [itemId, queue] of queues.current.entries()) {
      queue.pendingPatch = {};
      queue.resolvers.forEach(r => r.reject(new Error('Descartado pelo usuário')));
      queue.resolvers = [];
    }
    forceUpdate();
  }, [forceUpdate]);

  // Verifica se está processando
  const isProcessing = useCallback((itemId: string): boolean => {
    return queues.current.get(itemId)?.isProcessing || false;
  }, []);

  return {
    enqueue,
    hasPending,
    hasAnyPending,
    flushAll,
    getError,
    clearError,
    discardPending,
    discardAll,
    isProcessing,
    getPendingItemIds,
    _version: version // Para debug
  };
}
